/**
 * PURPOSE: Reglas y utilidades puras de la administración de cuentas de delegado (FTR-0013 + FTR-0017
 *   Phase 4: cuentas SISDEU dentro de la misma pantalla de Accesos).
 * RESPONSIBILITIES: Espejo de las reglas del backend (usuario 3–64 de a-z 0-9 . _ -, contraseña 4–128
 *   sin clases obligatorias), sugerencia de usuario <abrev>.<nombre>, generador de contraseñas fáciles
 *   de compartir, payloads de alta/edición, mensajes de error por estado/código y el reducer del
 *   formulario (la contraseña se vacía al guardar). Para cuentas SISDEU: etiquetas de método de acceso,
 *   estado del vínculo externo y modo de capacidades (política vs excepción), y la regla aprobada de que
 *   una excepción individual nunca puede quedar vacía (se desactiva la cuenta en su lugar). Sin red ni
 *   axios: se prueba con Jest.
 * COLLABORATORS: InstitutionAccessModal.jsx, portalAccountService.js, auditDisplay.utils.js,
 *   sisdeuAdmin.utils.js (vínculos/política/alertas, catálogo de capacidades compartido).
 * ROUTE: winscore/src/admin/AdminCompany/portalAccounts.utils.js
 */
export const PORTAL_CAPABILITY_OPTIONS = Object.freeze([
  // Copia de presentación únicamente (2026-09-17): la capacidad interna sigue siendo UNIFORMS_MANAGE en
  // todos lados (contrato, autorización, base de datos); solo cambia lo que ve el administrador.
  { value: "UNIFORMS_MANAGE", label: "Agregar uniformes" },
  { value: "MATCH_LINEUPS_MANAGE", label: "Gestionar alineaciones" },
]);
const CAPABILITY_VALUES = PORTAL_CAPABILITY_OPTIONS.map((option) => option.value);

export const PORTAL_CAPABILITY_LABELS = Object.freeze(
  Object.fromEntries(PORTAL_CAPABILITY_OPTIONS.map((option) => [option.value, option.label]))
);

export const USERNAME_PATTERN = /^[a-z0-9._-]{3,64}$/;
export const USERNAME_MAX_LENGTH = 64;
export const PASSWORD_MIN_LENGTH = 4;
export const PASSWORD_MAX_LENGTH = 128;
export const DISPLAY_NAME_MAX_LENGTH = 128;
export const USERNAME_RULE_MESSAGE =
  "El usuario debe tener entre 3 y 64 caracteres: letras minúsculas, números, punto, guion o guion bajo.";

// Sin caracteres que se confunden al dictarlos o copiarlos a mano (0/o, 1/l/i).
export const PASSWORD_ALPHABET = "abcdefghjkmnpqrstuvwxyz23456789";
export const GENERATED_PASSWORD_LENGTH = 10;

export const normalizePortalUsername = (value) =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

export const validatePortalUsername = (value) =>
  USERNAME_PATTERN.test(normalizePortalUsername(value)) ? null : USERNAME_RULE_MESSAGE;

/** Mismo criterio que el backend: cuenta puntos de código y nunca recorta. */
export const validatePortalPassword = (value) => {
  if (typeof value !== "string" || value.length === 0) return "La contraseña es obligatoria.";
  const length = [...value].length;
  if (length < PASSWORD_MIN_LENGTH) {
    return `La contraseña debe tener al menos ${PASSWORD_MIN_LENGTH} caracteres.`;
  }
  if (length > PASSWORD_MAX_LENGTH) {
    return `La contraseña no puede superar ${PASSWORD_MAX_LENGTH} caracteres.`;
  }
  return null;
};

const toSlug = (value) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

const firstWord = (value) => String(value ?? "").trim().split(/\s+/)[0] || "";

/** Sugerencia <abrev>.<nombre>: abreviatura (o primera palabra de la institución) y primer nombre. */
export const suggestPortalUsername = ({ institution, displayName }) => {
  const prefix = toSlug(institution?.abrev) || toSlug(firstWord(institution?.name));
  const name = toSlug(firstWord(displayName));
  const suggestion = [prefix, name].filter(Boolean).join(".").slice(0, USERNAME_MAX_LENGTH);
  return USERNAME_PATTERN.test(suggestion) ? suggestion : "";
};

const defaultGetRandomValues = (bytes) => {
  const cryptoApi = typeof window !== "undefined" ? window.crypto : undefined;
  if (!cryptoApi?.getRandomValues) {
    throw new Error("El navegador no ofrece un generador aleatorio seguro.");
  }
  return cryptoApi.getRandomValues(bytes);
};

/** Contraseña aleatoria (CSPRNG, muestreo por rechazo para no sesgar el alfabeto). */
export const generatePortalPassword = ({
  length = GENERATED_PASSWORD_LENGTH,
  getRandomValues = defaultGetRandomValues,
} = {}) => {
  const alphabetSize = PASSWORD_ALPHABET.length;
  const limit = 256 - (256 % alphabetSize);
  let password = "";
  while (password.length < length) {
    const bytes = new Uint8Array(length * 2);
    getRandomValues(bytes);
    for (const byte of bytes) {
      if (byte < limit && password.length < length) password += PASSWORD_ALPHABET[byte % alphabetSize];
    }
  }
  return password;
};

export const orderCapabilities = (list = []) =>
  CAPABILITY_VALUES.filter((capability) => list.includes(capability));

const sameList = (left, right) =>
  left.length === right.length && left.every((item) => right.includes(item));

export const EMPTY_ACCOUNT_FORM = Object.freeze({
  username: "",
  displayName: "",
  email: "",
  phone: "",
  password: "",
  capabilities: Object.freeze([]),
});

export const accountToForm = (account) => ({
  username: account?.username || "",
  displayName: account?.displayName || "",
  email: account?.email || "",
  phone: account?.phone || "",
  password: "",
  capabilities: orderCapabilities(account?.capabilities || []),
});

/** Reducer del formulario de cuenta: alta (con contraseña) o edición (sin contraseña). */
export const accountFormReducer = (state, action) => {
  switch (action.type) {
    case "reset":
      return { ...EMPTY_ACCOUNT_FORM, capabilities: [] };
    case "load":
      return accountToForm(action.account);
    case "change":
      return { ...state, [action.field]: action.value };
    case "toggleCapability": {
      const capabilities = state.capabilities.includes(action.capability)
        ? state.capabilities.filter((capability) => capability !== action.capability)
        : orderCapabilities([...state.capabilities, action.capability]);
      return { ...state, capabilities };
    }
    case "clearPassword":
      return { ...state, password: "" };
    default:
      return state;
  }
};

/**
 * `isSisdeu` (FTR-0017 Phase 4) omite la validación de usuario: el campo es de solo lectura para estas
 * cuentas y su valor real, `sisdeu:<id>`, cae deliberadamente fuera del charset local (":" no está
 * permitido) — validarlo aquí bloquearía CUALQUIER guardado (nombre, correo, capacidades) de una cuenta
 * SISDEU con un error de usuario imposible de corregir desde este formulario.
 */
export const validateAccountForm = (form, { requirePassword = false, isSisdeu = false } = {}) => {
  const errors = {};
  const displayName = form.displayName.trim();
  if (!displayName) errors.displayName = "El nombre del delegado es obligatorio.";
  else if (displayName.length > DISPLAY_NAME_MAX_LENGTH) {
    errors.displayName = `El nombre no puede superar ${DISPLAY_NAME_MAX_LENGTH} caracteres.`;
  }
  if (!isSisdeu) {
    const usernameError = validatePortalUsername(form.username);
    if (usernameError) errors.username = usernameError;
  }
  const email = form.email.trim();
  if (email && (!email.includes("@") || /\s/.test(email))) errors.email = "El correo no es válido.";
  if (requirePassword) {
    const passwordError = validatePortalPassword(form.password);
    if (passwordError) errors.password = passwordError;
  }
  return errors;
};

export const buildCreatePayload = (form, idinstitution) => ({
  idinstitution,
  username: normalizePortalUsername(form.username),
  displayName: form.displayName.trim(),
  email: form.email.trim() || null,
  phone: form.phone.trim() || null,
  password: form.password,
  capabilities: orderCapabilities(form.capabilities),
});

/**
 * Solo los campos que cambiaron; la contraseña nunca viaja en la edición. Una cuenta SISDEU nunca envía
 * `username`: el backend la administra (409 ACCOUNT_EXTERNAL_IDENTITY) y la UI la muestra de solo
 * lectura, pero esta función no confía en eso — es la última barrera antes de la red.
 */
export const buildUpdatePayload = (form, account) => {
  const payload = {};
  if (account?.loginMethod !== "SISDEU") {
    const username = normalizePortalUsername(form.username);
    if (username !== account.username) payload.username = username;
  }
  const displayName = form.displayName.trim();
  if (displayName !== (account.displayName || "")) payload.displayName = displayName;
  const email = form.email.trim() || null;
  if (email !== (account.email ?? null)) payload.email = email;
  const phone = form.phone.trim() || null;
  if (phone !== (account.phone ?? null)) payload.phone = phone;
  const capabilities = orderCapabilities(form.capabilities);
  if (!sameList(capabilities, account.capabilities || [])) payload.capabilities = capabilities;
  return payload;
};

const CODE_MESSAGES = Object.freeze({
  DELEGATE_PORTAL_DISABLED: "El portal de delegados no está habilitado.",
  DELEGATE_PORTAL_COMPANY_DISABLED: "El portal de delegados no está habilitado para esta compañía.",
  USERNAME_TAKEN: "Ese usuario ya existe. Elige otro.",
  SESSION_REQUIRED: "Tu sesión expiró. Vuelve a iniciar sesión.",
  USER_NOT_ENABLED: "Tu usuario no está habilitado.",
  COMPANY_ADMIN_ROLE_REQUIRED: "Tu rol no puede administrar accesos de delegados.",
  COMPANY_NOT_ADMINISTRABLE: "No puedes administrar esta compañía.",
  // FTR-0017 Phase 2/4: acciones SISDEU-only sobre una cuenta puntual.
  ACCOUNT_EXTERNAL_IDENTITY:
    "La identidad de esta cuenta la administra SISDEU: no admite cambio de usuario ni contraseña local.",
  SISDEU_CAPABILITIES_EMPTY_OVERRIDE:
    "Una cuenta SISDEU no puede quedar sin capacidades por excepción: desactívala o ajusta la política de la compañía.",
  IDENTITY_BLOCKED:
    "La identidad SISDEU de esta cuenta está bloqueada por un cambio de institución: re-vincúlala antes de reactivar.",
  ACCOUNT_NOT_SISDEU: "Esta operación solo aplica a cuentas SISDEU.",
  IDENTITY_NOT_BLOCKED: "La identidad SISDEU de esta cuenta no está bloqueada; no hay nada que re-vincular.",
  INSTITUTION_NOT_LINKED: "La institución destino no tiene un vínculo SISDEU aprobado.",
  SISDEU_ACCOUNT_INACTIVE: "La cuenta SISDEU ya no está activa.",
  SISDEU_INSTITUTION_MISMATCH: "SISDEU no ubica esta cuenta en la institución destino.",
  SISDEU_LOGIN_COMPANY_DISABLED: "El acceso con cuentas SISDEU no está habilitado para esta compañía.",
  SISDEU_UNAVAILABLE: "SISDEU no está disponible en este momento. Intenta nuevamente en unos minutos.",
});

export const PORTAL_UNAVAILABLE_CODES = Object.freeze([
  "DELEGATE_PORTAL_DISABLED",
  "DELEGATE_PORTAL_COMPANY_DISABLED",
]);

export const isPortalUnavailableError = (error) =>
  PORTAL_UNAVAILABLE_CODES.includes(error?.response?.data?.code);

/** Mensaje para el administrador según estado HTTP y código estable del backend. */
export const getPortalAccountErrorMessage = (error, fallback) => {
  if (!error?.response) return "No se pudo conectar con el servidor.";
  const { status } = error.response;
  const data = error.response.data || {};
  if (data.code && CODE_MESSAGES[data.code]) return CODE_MESSAGES[data.code];
  if (status === 401) return CODE_MESSAGES.SESSION_REQUIRED;
  if (status === 429) return "Demasiadas solicitudes. Intenta nuevamente en unos segundos.";
  const serverMessage = data.message || data.msg;
  if ([400, 403, 404, 409].includes(status) && serverMessage) return serverMessage;
  if (status === 403) return "No tienes permiso para realizar esta acción.";
  if (status === 404) return "No se encontró la cuenta de delegado.";
  return fallback;
};

export const getAccountStatusLabel = (status) => (status === "ACTIVE" ? "Activa" : "Desactivada");

// --- FTR-0017 Phase 4: distinguir cuentas SISDEU dentro de la misma pantalla de Accesos --------------

export const getAccountLoginMethodLabel = (loginMethod) =>
  loginMethod === "SISDEU" ? "SISDEU" : "Local (Winscore)";

export const isSisdeuAccount = (account) => account?.loginMethod === "SISDEU";

export const getIdentityStatusLabel = (identity) =>
  identity?.status === "BLOCKED" ? "Vínculo bloqueado" : "Vínculo activo";

export const isIdentityBlocked = (identity) => identity?.status === "BLOCKED";

export const getCapabilitiesModeLabel = (capabilitiesMode) =>
  capabilitiesMode === "POLICY" ? "Política de la compañía" : "Excepción individual";

/**
 * Regla aprobada (FTR-0017 Business rules item 2 / Phase 2 decisión 2): una excepción individual nunca
 * puede quedar vacía. Se valida en el cliente ANTES de enviar, para no depender solo del 400 del
 * backend (`SISDEU_CAPABILITIES_EMPTY_OVERRIDE`); solo aplica a cuentas SISDEU con al menos una
 * capacidad hoy (si la cuenta ya no tenía ninguna, no hay cambio que rechazar).
 */
export const wouldLeaveSisdeuAccountWithEmptyOverride = (account, nextCapabilities) =>
  Boolean(isSisdeuAccount(account)) &&
  Array.isArray(nextCapabilities) &&
  nextCapabilities.length === 0 &&
  (account?.capabilities?.length || 0) > 0;

export const SISDEU_EMPTY_OVERRIDE_MESSAGE = CODE_MESSAGES.SISDEU_CAPABILITIES_EMPTY_OVERRIDE;
